import { useTranslation } from "react-i18next";

import { getRefreshBucketWindow, HaNContext, isInBucketWindow } from "@lib/logic";
import type { BuildingWorkspaceReservationResponse } from "@mapiq/user-api";
import { timeLabel } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useRequiredContext } from "@lib/utils";

import { useSelectPerson } from "../../../people/useSelectPerson";
import { PersonBookingCard } from "./PersonBookingCard";
import { PersonWorkdayTile } from "./PersonWorkdayTile";

type PersonTodayProps = {
  // The earliest first. None for a person found by the search who isn't in the building today.
  reservations: BuildingWorkspaceReservationResponse[];
};

export const PersonToday = ({ reservations }: PersonTodayProps) => {
  const { t } = useTranslation();
  const { bucket } = useRequiredContext(HaNContext);
  const selectPerson = useSelectPerson();

  const [first] = reservations;

  if (!first) {
    return (
      <section className="flex flex-col gap-4">
        <h3 className="text-[20px] font-semibold leading-[25px]">{t("translation:Today")}</h3>
        <p className="text-mapiq-black-500">{t("hereAndNow:NotInBuildingToday")}</p>
      </section>
    );
  }

  const end = reservations.reduce((latest, r) => (r.localEnd > latest ? r.localEnd : latest), first.localEnd);
  const bucketWindow = getRefreshBucketWindow(bucket);

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-[20px] font-semibold leading-[25px]">{t("translation:Today")}</h3>

      <PersonWorkdayTile
        status="OfficeDay"
        title={first.workspace.buildingName}
        subtitle={timeLabel(first.localStart, end, t)}>
        {reservations.map(reservation => (
          <PersonBookingCard
            key={`${reservation.workspace.nodeId}_${reservation.localStart}`}
            reservation={reservation}
            isNow={reservations.length > 1 && isInBucketWindow(reservation, bucketWindow)}
            onSelect={() => selectPerson({ owner: reservation.owner, reservation })}
          />
        ))}
      </PersonWorkdayTile>
    </section>
  );
};
